// libs
import React, { memo, forwardRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// styles
import {
  Label,
  Main,
  Bar,
  HelperText,
  FlexWrapper,
  Prefix,
  Suffix,
  Sibling,
  InputWrapper,
  InputElement,
  TextAreaElement,
  Counter,
} from './styles';

// def
import { IInputProps, ReqElement } from './types';
import { Theme } from '@nykaa/ui-components/themes/types';

//hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// helpers
import useHelper from './useHelper';

// constants
import { SPACING_NAMES } from './constants';

// Icon
import AlertNegativeFilledIcon from '@nykaa/ui-components/Icons/alert-negative-filled';

const ALERT_ICON_SIZE = '18px';

function BaseInput(
  {
    className = '',
    inputClassName = '',
    label,
    placeholder = '',
    type = 'text',
    value = '',
    onChange,
    isError = false,
    disabled = false,
    helperText = '',
    prefixComponent,
    suffixComponent,
    autoFocus = false,
    onFocus,
    leadingComponent = null,
    trailingComponent = null,
    ariaLabel,
    ariaDescribedby,
    multiline = false,
    spacingType = SPACING_NAMES.DEFAULT,
    showCounter = false,
    maxLength,
    onBlur,
    onKeyDown = () => { },
    ...restProps
  }: IInputProps,
  ref: React.RefObject<ReqElement>
) {
  const theme: Theme = useTheme();

  const isCompact = spacingType === SPACING_NAMES.COMPACT;

  const hasPrefixComponent = !isEmpty(prefixComponent);
  const hasSuffixComponent = !isEmpty(suffixComponent);

  const hasLeadingComponent = leadingComponent !== null;
  const hasTrailingComponent = trailingComponent !== null;

  const RequestedElement = multiline ? TextAreaElement : InputElement;

  const { barClassName, inFocus, isValid, setFocus, setOutOfFocus } = useHelper({
    hasPrefixComponent,
    hasSuffixComponent,
    autoFocus,
    value,
    onFocus,
    isError,
    onBlur,
  });

  return (
    <section className={`inputContainer ${className}`} data-test="mainBaseInput">
      <Main
        inFocus={inFocus}
        isDisabled={disabled}
        isValid={isValid}
        isError={isError}
        spacingType={spacingType}
        isMultiline={multiline}
        className="main"
      >
        <FlexWrapper
          hasLeadingComponent={hasLeadingComponent}
          hasTrailingComponent={hasTrailingComponent}
          className="flex-wrapper"
        >
          {hasLeadingComponent && (
            <Sibling className="leadingComponent">{leadingComponent}</Sibling>
          )}
          <InputWrapper className="inputWrapper">
            {hasPrefixComponent && <Prefix>{prefixComponent}</Prefix>}
            <RequestedElement
              aria-invalid={isError}
              aria-describedby={ariaDescribedby || helperText}
              aria-label={ariaLabel || label}
              inFocus={inFocus}
              type={type}
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className={`input ${inputClassName}`}
              onFocus={setFocus}
              onBlur={setOutOfFocus}
              data-test="inputElement"
              isMultiline={multiline}
              autoFocus={autoFocus}
              maxLength={maxLength}
              ref={ref}
              data-cs-mask
              {...restProps}
            />
            {hasSuffixComponent && <Suffix>{suffixComponent}</Suffix>}
            <Label
              isCompact={isCompact}
              className="label"
              inFocus={inFocus}
              isValid={isValid}
              isMultiline={multiline}
            >
              {label}
            </Label>
          </InputWrapper>
          {hasTrailingComponent && (
            <Sibling className="trailingComponent">{trailingComponent}</Sibling>
          )}
        </FlexWrapper>

        <Bar className={barClassName} isError={isError} inFocus={inFocus} isValid={isValid} />
      </Main>
      {helperText !== '' && (
        <HelperText className="helperText" isError={isError}>
          <span className="helperText-content">{helperText}</span>
          <AlertNegativeFilledIcon color={theme?.colors?.negative} size={ALERT_ICON_SIZE} />
        </HelperText>
      )}
      {helperText === '' && showCounter && (
        <Counter>
          {value.length}
          {maxLength && <span>/{maxLength}</span>}
        </Counter>
      )}
    </section>
  );
}

export default memo(forwardRef(BaseInput));
